import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { DateRange } from 'react-day-picker';
import Spinner from 'react-spinkit';
import { Icon } from '@iconify/react';
import { RootState } from '../../ApplicationState';
import { getCookie, getLocalForage, setLocalForage } from '../../aaa';
import {
  filterMixpanelEventsByDate,
  getDefaultSchoolYear,
  getDefaultSchoolYearMinusOne,
  getDistrictDataByType,
  getDistrictDataBySchoolFromEvents,
  getLegacySchoolsPeekavilleScript,
  getLegacySchoolsPlatformScript,
  getReportFetchWindow,
  getSchoolYearDateBounds,
  getStartOfSchoolYear,
  getTopFiveSchools,
  getTotalRow,
  getUpdatedDistrictUnifiedScript,
  lookupNewDBStartDate,
} from '../reportUtilities';
import { getDistricts, getSchools, queryMixpanelByGroup, queryMixpanelByGroupV2 } from '../../BackendInterface';
import DistrictChart from './DistrictChart';
import DistrictSelector from '../AdminReportComponents/DistrictSelector/DistrictSelector';
import DateRangePicker from '../../components/DateRangePicker';
import DistrictDataTable from './DistrictDataTable';
import CustomDropdown from '../AdminReportComponents/CustomDropdown/CustomDropdown';
import HowToUseModal from './HowToUseModal';
import DownloadReportModal from '../SchoolReport/DownloadReportModal';
import DistrictReportPDFSlides from './DistrictReportPDFSlides';
import SpinnerOverlay from '../../SpinnerOverlay';
import {
  DistrictDeckMetrics,
  DistrictPdfAggregates,
  computeDistrictDeckMetrics,
  groupEventsBySchool,
} from './districtReportDeck';
import { loadDistrictAssignmentsCount, loadDistrictPdfAggregates, loadEducatorNames } from './districtPdfData';
import { isUserHasKey, logDebugLevel } from '../../GlobalFunctions';
import styles from './DistrictReport.module.scss';
import libraryStyles from '../../SCSS/Library.module.scss';
import { Districts, ReportingMixpanelEvent } from '@peekapak/types/ReportDataExchangeTypes';
import { SchoolModel } from '@peekapak/types/DataProtocolTypes';

const tableOverrideSuffix = '';

const startOfSchoolYear = getStartOfSchoolYear();

const getDataKey = (districtId: string, from?: Date, to?: Date) =>
  `${districtId}|${from?.toISOString() ?? ''}|${to?.toISOString() ?? ''}`;

interface FetchedEvents {
  key: string;
  legacyPlatform: ReportingMixpanelEvent[];
  legacyPeekaville: ReportingMixpanelEvent[];
  newMixpanel: ReportingMixpanelEvent[];
}

const DistrictReport = () => {
  // "Today" is when this page was opened (not when the module was first loaded), the same as the
  // school report's default range ends.
  const [today] = useState(() => new Date());
  const [dateRange, setDateRange] = useState<DateRange>(() => ({ from: startOfSchoolYear, to: today }));
  const [isDisplayDayPicker, setIsDisplayDayPicker] = useState(false);

  const [districtList, setDistrictList] = useState<Districts>([]);
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [isDistrictListLoaded, setDistrictListLoaded] = useState(false);

  const [schoolList, setSchoolList] = useState<SchoolModel[]>([]);
  // Which district schoolList belongs to - events are only fetched (and split into rows) once the
  // list for the currently selected district is in.
  const [schoolListDistrict, setSchoolListDistrict] = useState('');
  const [isSchoolListLoaded, setSchoolListLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [fetchedEvents, setFetchedEvents] = useState<FetchedEvents | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  // Same school-year handling as the school report: the current school year by default, switched to
  // the previous one when a start date before this school year is picked, and back to the current
  // one on a district change or reload.
  const [selectedSchoolYear, setSelectedSchoolYear] = useState(getDefaultSchoolYear);

  const [mixpanelError, setMixpanelError] = useState<string>('');

  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  // The PDF-only data, tagged with the district/date range it was requested for. `aggregates` is null
  // while the request is in flight.
  const [pdfData, setPdfData] = useState<{
    key: string;
    aggregates: DistrictPdfAggregates | null;
    error: string;
  } | null>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfStatusMessage, setPdfStatusMessage] = useState('');
  const [deckMetrics, setDeckMetrics] = useState<DistrictDeckMetrics | null>(null);
  // A failed PDF attempt - shown in the Download dialog, not the page banner (which is about the
  // on-page data), and cleared on the next attempt or the next time the dialog is opened.
  const [pdfError, setPdfError] = useState('');

  const [eventToChart, setEventToChart] = useState<string>('educator_logins');

  const history = useHistory();

  const handleSchoolClick = (schoolName: string) => {
    const school = schoolList.find((s) => s.name === schoolName);
    if (school?.id) {
      history.push('/reports/School', { schoolId: school.id });
    }
  };

  const eventNames = [
    {
      name: 'Number of Staff',
      id: 'number_of_educators',
    },
    {
      name: 'Staff Logins',
      id: 'educator_logins',
    },
    {
      name: 'Student Logins',
      id: 'student_logins',
    },
    {
      name: 'Story Opens',
      id: 'story_opens',
    },
    {
      name: 'Lessons Accessed',
      id: 'lessons_accessed',
    },
    {
      name: 'Time on Platform (hrs)',
      id: 'time_on_platform',
    },
  ];

  const userProfile = useSelector((state: RootState) => {
    return state.user.userProfile;
  });

  async function onSelectDistrict(district: string) {
    if (isDistrictListLoaded && districtList.length > 0) {
      setSelectedDistrict(district);
      setSelectedSchoolYear(getDefaultSchoolYear());
      const storageKey = 'selectedDistrict';
      await setLocalForage(storageKey, district);
      setSchoolListLoaded(false);
    } else {
      console.warn(`%cDistrict List is Missing`, 'background: black; color:yellow');
    }
  }

  //////////////////////////////////////////////////////////////////
  //
  // initialize the list of districts
  //
  //////////////////////////////////////////////////////////////////
  useEffect(() => {
    let mounted = true;

    if (!isDistrictListLoaded) {
      getListOfDistricts();
    } else {
      restoreSavedSelectedDistrict();
    }

    return function cleanup() {
      mounted = false;
    };

    async function getListOfDistricts() {
      const storageKey = 'districtsList';
      const map = await getLocalForage(storageKey);

      if (map && mounted) {
        setDistrictList(map);
        setDistrictListLoaded(true);
      }

      const mapFromDb = await getDistricts();
      await setLocalForage(storageKey, mapFromDb, 1440);

      if (mounted) {
        setDistrictList(mapFromDb);
        setDistrictListLoaded(true);
      }
    }

    async function restoreSavedSelectedDistrict() {
      const storageKey = 'selectedDistrict';
      const selectedDistrict = await getLocalForage(storageKey);

      if (selectedDistrict) {
        setSelectedDistrict(selectedDistrict);
      }
    }
  }, [districtList, isDistrictListLoaded, userProfile, selectedDistrict]);

  //////////////////////////////////////////////////////////////////
  //
  // initialize the list of schools in the selected district
  //
  //////////////////////////////////////////////////////////////////
  useEffect(() => {
    let mounted = true;

    if (!isSchoolListLoaded || (selectedDistrict && schoolListDistrict !== selectedDistrict)) {
      getListOfSchoolsInDistrict();
    }

    return function cleanup() {
      mounted = false;
    };

    // load list of schools
    async function getListOfSchoolsInDistrict() {
      const selectedDistrictStorageKey = 'selectedDistrict';
      const districtForQuery =
        selectedDistrict || (await getLocalForage(selectedDistrictStorageKey)) || userProfile?.district || '';

      // if there is no district for getting the list of schools, then there's
      // no point to continuing (check?)
      if (!districtForQuery) {
        return;
      }

      const schoolListStorageKey = `${districtForQuery}-schoolsList`;
      const list = (await getLocalForage(schoolListStorageKey))?.filter((s: SchoolModel) => !s.isArchived);

      if (list?.length > 0 && mounted) {
        setSchoolList(list);
        setSchoolListDistrict(districtForQuery as string);
        setSchoolListLoaded(true);
        return;
      }

      // if the list is not in local storage, then get it from the database
      const listFromDb = (await getSchools(districtForQuery, tableOverrideSuffix))?.filter((s) => !s.isArchived);
      await setLocalForage(schoolListStorageKey, listFromDb, 43200);

      if (mounted) {
        setSchoolList(listFromDb);
        setSchoolListDistrict(districtForQuery as string);
        setSchoolListLoaded(true);
      }
    }
  }, [isDistrictListLoaded, userProfile, selectedDistrict, isSchoolListLoaded, schoolListDistrict]);

  //////////////////////////////////////////////////////////////////
  //
  // AFTER the district list is loaded, set the selected district
  //
  //////////////////////////////////////////////////////////////////
  useEffect(() => {
    run();

    async function run() {
      if (isDistrictListLoaded && districtList.length > 0) {
        const storageKey = 'selectedDistrict';
        const selectedDistrict: string = (await getLocalForage(storageKey)) as unknown as string;

        if (selectedDistrict) {
          setSelectedDistrict(selectedDistrict);
        } else {
          const userDistrict = districtList.some((district) => district.id === userProfile?.district)
            ? (userProfile?.district ?? '')
            : '';
          return setSelectedDistrict(userDistrict);
        }
      }
    }
  }, [userProfile, isDistrictListLoaded, districtList]);

  const handleLoadData = () => {
    setMixpanelError('');
    setSelectedSchoolYear(getDefaultSchoolYear());
    setRefreshCount((count) => count + 1);
  };

  const handleBeforeStart = () => setSelectedSchoolYear(getDefaultSchoolYearMinusOne());

  //////////////////////////////////////////////////////////////////
  //
  // Load events the same way the school report does, so every school's
  // row here is exactly what its own school report shows:
  //  - selected by the schools on this district's list (by school id,
  //    with each school's own filter), not by the events' district tag
  //  - over the same fetch window (the selected school year, stretched to
  //    cover the picked dates), from the same three Mixpanel projects
  //  - then narrowed to the picked dates in the browser, with the same
  //    filter. So changing the dates only refetches when the picked range
  //    falls outside the window already loaded.
  //
  //////////////////////////////////////////////////////////////////
  const schoolYearBounds = getSchoolYearDateBounds(selectedSchoolYear);
  const fetchWindow = getReportFetchWindow(schoolYearBounds.start, schoolYearBounds.end, dateRange);
  const isSchoolListCurrent = isSchoolListLoaded && !!selectedDistrict && schoolListDistrict === selectedDistrict;
  const fetchKey = isSchoolListCurrent
    ? `${selectedDistrict}|${fetchWindow.start}|${fetchWindow.end}|${refreshCount}`
    : '';

  useEffect(() => {
    if (!fetchKey) return;
    let cancelled = false;
    const { start, end } = fetchWindow;
    const schools = schoolList.map((school) => school.combineIds || school.id);
    logDebugLevel(`District Report retrieving data for ${selectedDistrict} from ${start} to ${end}`);

    const run = async () => {
      setIsFetching(true);
      try {
        if (!schools.length) {
          if (!cancelled)
            setFetchedEvents({ key: fetchKey, legacyPlatform: [], legacyPeekaville: [], newMixpanel: [] });
          return;
        }
        // Run side by side - the legacy projects are small, so they add no wait to the unified query.
        // Same per-project rules as the school report: the legacy platform project is only queried for
        // windows starting before it was retired, the legacy myPeekaville project always.
        const [newMixpanel, legacyPlatform, legacyPeekaville] = await Promise.all([
          queryMixpanelByGroupV2(getUpdatedDistrictUnifiedScript(start, end, schools), 'unified', tableOverrideSuffix),
          new Date(start) > lookupNewDBStartDate('platformEvents')
            ? Promise.resolve([] as ReportingMixpanelEvent[])
            : queryMixpanelByGroup(
                getLegacySchoolsPlatformScript(start, end, schools),
                'peekapak',
                tableOverrideSuffix,
              ),
          queryMixpanelByGroup(
            getLegacySchoolsPeekavilleScript(start, end, schools),
            'mypeekaville',
            tableOverrideSuffix,
          ),
        ]);
        if (!cancelled) setFetchedEvents({ key: fetchKey, legacyPlatform, legacyPeekaville, newMixpanel });
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setMixpanelError('Uh oh! Something went wrong. Please try again later or contact Peekapak support.');
          setFetchedEvents({ key: fetchKey, legacyPlatform: [], legacyPeekaville: [], newMixpanel: [] });
        }
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    };
    run();

    return () => {
      cancelled = true;
    };
    // fetchKey captures everything the query depends on (district, its school list, the window).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  const isDataLoaded = !!fetchedEvents && fetchedEvents.key === fetchKey;
  const isLoading = isFetching || !isDataLoaded;

  // The school report's filterDataByDate: the picked range, applied to every project's events.
  const dateFilteredEvents = useMemo(() => {
    if (!isDataLoaded || !fetchedEvents || !dateRange.from || !dateRange.to) return null;
    const inRange = (events: ReportingMixpanelEvent[]) =>
      filterMixpanelEventsByDate(dateRange.from as Date, dateRange.to as Date, events || []);
    return {
      legacyPlatform: inRange(fetchedEvents.legacyPlatform),
      legacyPeekaville: inRange(fetchedEvents.legacyPeekaville),
      newMixpanel: inRange(fetchedEvents.newMixpanel),
    };
  }, [isDataLoaded, fetchedEvents, dateRange]);

  const dataByType = useMemo(
    () =>
      dateFilteredEvents
        ? getDistrictDataByType(
            dateFilteredEvents.legacyPeekaville,
            dateFilteredEvents.legacyPlatform,
            dateFilteredEvents.newMixpanel,
          )
        : undefined,
    [dateFilteredEvents],
  );
  const dataBySchool = useMemo(
    () =>
      dateFilteredEvents
        ? getDistrictDataBySchoolFromEvents(
            dateFilteredEvents.legacyPeekaville,
            dateFilteredEvents.legacyPlatform,
            dateFilteredEvents.newMixpanel,
            schoolList,
          )
        : undefined,
    [dateFilteredEvents, schoolList],
  );
  const topFiveSchools = useMemo(
    () => (dataBySchool ? getTopFiveSchools(dataBySchool, eventToChart) : []),
    [dataBySchool, eventToChart],
  );

  // Which district/date range the on-page numbers are currently showing. The PDF-only query below
  // waits for this, so it never runs alongside (and competes with) the on-page queries.
  const loadedDataKey = dateFilteredEvents ? getDataKey(selectedDistrict, dateRange.from, dateRange.to) : '';

  //////////////////////////////////////////////////////////////////
  //
  // PDF-only data: moods, games and stories, which the
  // on-page report doesn't use. Requested only once someone opens the
  // Download dialog - every Mixpanel query counts against a limit shared
  // by everyone using the reports (60 an hour per project), so page views
  // that never download don't spend any of it. Cached for an hour after.
  //
  //////////////////////////////////////////////////////////////////
  useEffect(() => {
    if (!isDownloadModalOpen || !loadedDataKey || !dateRange.from || !dateRange.to) return;
    // Already loaded, or still loading, for this selection - a failed attempt is retried.
    if (pdfData?.key === loadedDataKey && !pdfData.error) return;

    const key = loadedDataKey;
    setPdfData({ key, aggregates: null, error: '' });
    // Deliberately not cancelled when the dialog closes: the result is still wanted (and cached) for
    // when it's reopened. A result for a selection that has since changed is just dropped.
    loadDistrictPdfAggregates(selectedDistrict, schoolList, dateRange.from, dateRange.to)
      .then((aggregates) => {
        setPdfData((prev) => (prev?.key === key ? { key, aggregates, error: '' } : prev));
      })
      .catch((error) => {
        console.error(error);
        setPdfData((prev) =>
          prev?.key === key ? { key, aggregates: null, error: (error as Error).message || 'Unknown error' } : prev,
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDownloadModalOpen, loadedDataKey]);

  const toggleShowModal = () => {
    setShowModal(true);
  };

  useEffect(() => {
    const cookie = getCookie('howToUseDistrictReporting');
    if (cookie === 'neverShowAgain') {
      setShowModal(false);
    } else {
      setShowModal(true);
    }
  }, []);

  const handleDownloadCSV = useCallback(() => {
    if (!isDataLoaded || !dataBySchool || isLoading) return;

    try {
      const headers = [
        'School Name',
        'Number of Staff',
        'Staff Logins',
        'Student Logins',
        'Story Opens',
        'Lessons Accessed',
        'Time on Platform (hrs)',
      ];
      const totalRow = getTotalRow(dataBySchool);

      // Quoted, so a school name with a comma in it doesn't shift its row's columns.
      const csvText = (value: string) => `"${String(value).replace(/"/g, '""')}"`;
      const rows = dataBySchool.map((school) => [
        csvText(school.school_name),
        school.number_of_educators,
        school.educator_logins,
        school.student_logins,
        school.story_opens,
        school.lessons_accessed,
        school.time_on_platform,
      ]);

      // getTotalRow returns ['Total', ...column sums] in the same column order as the rows above.
      if (totalRow) {
        rows.push(['TOTAL', ...totalRow.slice(1)]);
      }

      const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const districtName = districtList.find((d) => d.id === selectedDistrict)?.name || 'District';
      const startDateStr = dateRange.from ? dateRange.from.toLocaleDateString() : '';
      const endDateStr = dateRange.to ? dateRange.to.toLocaleDateString() : '';
      link.download = `${districtName}-Report-${startDateStr}-to-${endDateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating CSV:', error);
    }
  }, [isDataLoaded, dataBySchool, isLoading, dateRange, districtList, selectedDistrict]);

  const districtName = districtList.find((d) => d.id === selectedDistrict)?.name || 'District';
  const isReportReady = isDataLoaded && !isLoading && !!dataByType && !!dataBySchool;
  const currentPdfData = pdfData?.key === loadedDataKey ? pdfData : null;
  const pdfAggregates = currentPdfData?.aggregates ?? null;
  const isPdfDataUnavailable = !!currentPdfData?.error;
  const isPdfDataLoading = !pdfAggregates && !isPdfDataUnavailable;

  const handleDownloadPDF = useCallback(async () => {
    if (!isReportReady || !dataByType || !dataBySchool || !pdfAggregates) return;

    setIsDownloadModalOpen(false);
    setPdfError('');
    setPdfStatusMessage('Loading additional school specific data...');
    setIsGeneratingPDF(true);
    try {
      // Only schools that will actually get a top-educators table need their roster looked up.
      const educatorLoginsBySchool = groupEventsBySchool(dataByType.educator_logins, schoolList);
      const schoolsWithEducatorLogins = schoolList.filter((_, i) => educatorLoginsBySchool[i].events.length > 0);
      // Both run side by side; they share the per-school user lists (and their cache).
      const [educatorNames, assignments] = await Promise.all([
        loadEducatorNames(schoolsWithEducatorLogins),
        loadDistrictAssignmentsCount(schoolList, fetchWindow, dateRange.from as Date, dateRange.to as Date),
      ]);

      setPdfStatusMessage('Generating PDF report...');
      setDeckMetrics(
        computeDistrictDeckMetrics({
          districtName,
          startDate: dateRange.from || null,
          endDate: dateRange.to || null,
          schoolList,
          dataByType,
          dataBySchool,
          aggregates: pdfAggregates,
          assignments,
          educatorNames,
        }),
      );
    } catch (error) {
      console.error('Error preparing the district PDF report:', error);
      setPdfError('Something went wrong creating the PDF. Please try again, or contact Peekapak support.');
      setIsGeneratingPDF(false);
      setIsDownloadModalOpen(true);
    }
  }, [isReportReady, dataByType, dataBySchool, pdfAggregates, schoolList, districtName, dateRange, fetchWindow]);

  const handlePDFComplete = useCallback(() => {
    setDeckMetrics(null);
    setIsGeneratingPDF(false);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.titleAndSelectors}>
        <h1>District Reporting</h1>
        {isDistrictListLoaded && (
          <DistrictSelector
            isPeekapakAdmin={!!isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')}
            districtsList={districtList}
            onSelectDistrict={onSelectDistrict}
            initialValue={selectedDistrict}
            currentlySelectedDistrict={selectedDistrict}
            disableChangeLocation={false}
          />
        )}
        <div className={styles.eventType}>
          <CustomDropdown
            list={eventNames}
            onSelectSubject={setEventToChart}
            initialValue={eventToChart}
            currentlySelectedItem={eventToChart}
          />
        </div>
      </div>
      <div className={styles.dateAndSubmit}>
        <DateRangePicker
          dateRange={dateRange}
          setDateRange={setDateRange}
          startDate={startOfSchoolYear}
          excludeAfter={today}
          beforeFirstInputText='Start Date:'
          beforeSecondInputText='End Date:'
          isDisplayCalendar={isDisplayDayPicker}
          setIsDisplayCalendar={setIsDisplayDayPicker}
          handleBeforeStart={handleBeforeStart}
        />
        <Icon className={styles.reloadIcon} icon='zondicons:reload' onClick={handleLoadData} />
        <Icon
          className={`${styles.reloadIcon} ${!isReportReady ? styles.disabled : ''}`}
          icon='zondicons:download'
          onClick={() => {
            if (!isReportReady) return;
            setPdfError('');
            setIsDownloadModalOpen(true);
          }}
        />
        {mixpanelError && <p className={styles.mixpanelError}>{mixpanelError}</p>}
        <div className={styles.doNotShowAgain}>
          <button onClick={toggleShowModal} className={libraryStyles.textLink}>
            How do I use this tool?
          </button>
        </div>
      </div>
      {isDataLoaded && dataByType && dataBySchool && !isLoading ? (
        <>
          <DistrictChart
            data={dataByType}
            eventToChart={eventToChart}
            startDate={dateRange.from}
            endDate={dateRange.to}
            schoolList={schoolList}
            topFiveSchools={topFiveSchools}
          />
          <DistrictDataTable data={dataBySchool} topFiveSchools={topFiveSchools} onSchoolClick={handleSchoolClick} />
        </>
      ) : (
        <div className={styles.loadingContainer}>
          <Spinner />
        </div>
      )}
      <HowToUseModal show={showModal} handleHideModal={() => setShowModal(false)} />
      <DownloadReportModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        onDownloadCSV={() => {
          setIsDownloadModalOpen(false);
          handleDownloadCSV();
        }}
        onDownloadPDF={handleDownloadPDF}
        pdfDisabled={isPdfDataLoading || isPdfDataUnavailable}
        errorMessage={pdfError}
        pdfDisabledMessage={
          isPdfDataUnavailable
            ? 'PDF data failed to load. Try reloading the report.'
            : 'PDF data is still loading, please wait.'
        }
      />
      {deckMetrics && isGeneratingPDF && (
        <DistrictReportPDFSlides
          metrics={deckMetrics}
          fileName={`${deckMetrics.districtName}-Report.pdf`}
          onComplete={handlePDFComplete}
        />
      )}
      <SpinnerOverlay isShow={isGeneratingPDF} message={pdfStatusMessage} />
    </div>
  );
};

// note the lock decorator now expressed as an HOC call at the end of the source
// // code file

// const LockableDistrictReport = lockWithPreviewUnlessHasAny(
//   'KEY_ADMINISTRATOR_REPORTS',
//   'KEY_PEEKAPAK_MANAGEMENT',
//   'KEY_WIP',
//   'KEY_MASTER_KEY'
// )(DistrictReport);

export default DistrictReport;
